/**
 * Standard supplier-category taxonomy for AskIsaac / Sterile Mate.
 *
 * One curated list shared between:
 *   - The Add/Edit Supplier forms (multi-select picker)
 *   - The Source-From-Suppliers RFQ modal (filter dropdown + Select-all-in-category)
 *   - Any analytics / reporting on supplier mix
 *
 * Values are stored as the `id` strings on the supplier document
 * (db.suppliers.categories: ["surgical_or", "ppe_apparel", ...]).
 *
 * Free-text custom tags (strings outside this list) are allowed — the UI
 * displays them with a "Custom" badge.
 */

export const SUPPLIER_CATEGORIES = [
  { id: "surgical_or", label: "Surgical & OR", description: "Instruments, sponges, pledgets, sutures, drapes, gowns" },
  { id: "needles_injection", label: "Needles, Syringes & Injection", description: "Needles, syringes, IV catheters, infusion sets" },
  { id: "medication_delivery", label: "Medication & Pharmaceutical Delivery", description: "Pumps, vials, ampoules, controlled-substance handling" },
  { id: "wound_care", label: "Wound Care & Dressings", description: "Bandages, gauze, advanced dressings, NPWT" },
  { id: "ppe_apparel", label: "PPE & Apparel", description: "Gloves, masks, isolation gowns, shoe covers" },
  { id: "janitorial_evs", label: "Janitorial & EVS", description: "Cleaners, disinfectants, mops, vacuum bags, floor pads" },
  { id: "sterile_processing", label: "Sterile Processing (SPD)", description: "Wraps, indicators, instrument trays, AAMI ST108 supplies" },
  { id: "dialysis_water", label: "Dialysis & Water Treatment", description: "DI tanks, RO membranes, softeners, water-quality testing" },
  { id: "lab_diagnostics", label: "Lab & Diagnostics", description: "Reagents, tubes, slides, swabs, point-of-care" },
  { id: "imaging_radiology", label: "Imaging & Radiology", description: "Contrast media, films, lead aprons, accessories" },
  { id: "patient_care", label: "Patient Care & Daily Living", description: "Bedding, briefs, basins, ADLs" },
  { id: "capital_equipment", label: "Capital Equipment", description: "Beds, pumps, lights, refurbished majors" },
  { id: "facilities_maintenance", label: "Facilities & Maintenance", description: "HVAC parts, plumbing, electrical, water-treatment chemicals" },
  { id: "office_admin", label: "Office, Forms & Admin", description: "Paper, forms, printers, ID badges" },
  { id: "food_service", label: "Food Service", description: "Disposable trays, utensils, hydration" },
];

/** Fast lookup by id → label */
export const CATEGORY_LABEL_BY_ID = SUPPLIER_CATEGORIES.reduce((acc, c) => {
  acc[c.id] = c.label;
  return acc;
}, {});

/** Render a stored category id (curated or custom) as a human label. */
export function labelForCategory(id) {
  if (!id) return "";
  return CATEGORY_LABEL_BY_ID[id] || id.replace(/_/g, " ");
}

/** Is the given id one of the curated categories? */
export function isCuratedCategory(id) {
  return !!CATEGORY_LABEL_BY_ID[id];
}
